import React from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaClipboardList, FaChartBar, FaFileAlt, FaCalendarAlt } from 'react-icons/fa';
import { getBrand } from '../utils/brandStyles';
import PageHero from '../components/PageHero';
import CTABanner from '../components/CTABanner';

const FreeAssessment = ({ market = 'lu' }) => {
  const b = getBrand(market);

  // 4 steps → 2×2 grid
  const steps = [
    {
      icon: <FaCalendarAlt size={36} color={b.accent} />,
      title: 'Schedule a Call',
      description: '30-minute initial consultation to understand your business needs.'
    },
    {
      icon: <FaClipboardList size={36} color={b.accent} />,
      title: 'IT Environment Review',
      description: 'We analyse your current setup, infrastructure, and pain points.'
    },
    {
      icon: <FaChartBar size={36} color={b.accent} />,
      title: 'Analysis & Recommendations',
      description: 'Receive a detailed report with prioritised improvement opportunities.'
    },
    {
      icon: <FaFileAlt size={36} color={b.accent} />,
      title: 'Roadmap & Next Steps',
      description: 'Clear action plan with costs, timelines, and expected outcomes.'
    }
  ];

  // 6 review areas → 3-col grid
  const whatWeReview = [
    'Current cloud usage and potential savings',
    'Security vulnerabilities and compliance gaps',
    'Opportunities for AI and automation',
    'Backup and disaster recovery readiness',
    'IT infrastructure bottlenecks',
    'Software licensing optimisation'
  ];

  // 4 benefits → 2×2 grid
  const benefits = [
    'No obligation, completely free',
    'Actionable insights you can implement immediately',
    'Clear ROI estimates for recommended changes',
    'Honest assessment — we only recommend what you need'
  ];

  return (
    <div style={{ fontFamily: b.fontBody, color: b.text, background: b.bg }}>

      {/* ── HERO ── */}
      <PageHero
        market={market}
        title="Free IT Assessment for Your Business"
        subtitle="Get a clear picture of your IT environment and discover opportunities to save money, improve security, and work more efficiently."
        ctaText="Request Your Free Assessment"
        ctaLink="/contact"
        icon={<FaClipboardList color="#1A237E" />}
      />

      {/* ── HOW IT WORKS — 4 → 2×2 ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>The Process</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              How it works
            </h2>
          </div>
          <div className="grid-4">
            {steps.map((step, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.07 }} viewport={{ once: true }}
                className={b.isMWO ? 'feature-card mwo-card' : 'feature-card mindit-card'}
                style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 24px', textAlign: 'center' }}
              >
                <div style={{
                  width: '72px', height: '72px', borderRadius: b.isMWO ? '0' : '50%',
                  background: b.isMWO ? 'rgba(0,188,212,0.1)' : '#EFF6FF',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                  border: b.isMWO ? `1px solid ${b.accent}` : 'none'
                }}>
                  {step.icon}
                </div>
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>{step.title}</h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted }}>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT WE REVIEW — 6 → 3-col ── */}
      <section style={{ background: b.bg, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Scope of Review</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              What we review
            </h2>
          </div>
          <div className="grid-6">
            {whatWeReview.map((item, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }} viewport={{ once: true }}
                className="checklist-item"
                style={{ background: b.checklistBg, borderRadius: b.isMWO ? '0' : '8px', borderLeft: b.isMWO ? `3px solid ${b.accent}` : 'none' }}
              >
                <FaCheckCircle size={16} color={b.accent} className="checklist-icon" />
                <span style={{ fontSize: b.fz.sm, lineHeight: 1.6, fontWeight: 500 }}>{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY DIFFERENT — 4 → 2×2 ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Our Difference</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Why our assessment is different
            </h2>
          </div>
          <div className="grid-4">
            {benefits.map((benefit, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, delay: i * 0.07 }} viewport={{ once: true }}
                style={{
                  display: 'flex', gap: '14px', alignItems: 'flex-start',
                  background: b.bgCard, borderRadius: b.cardRadius,
                  boxShadow: b.cardShadow, padding: '24px 20px'
                }}
              >
                <FaCheckCircle size={20} color="#4CAF50" style={{ flexShrink: 0, marginTop: '2px' }} />
                <span style={{ fontSize: b.fz.base, lineHeight: 1.6, color: b.text }}>{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <CTABanner
        market={market}
        title="Ready to optimise your IT?"
        body="No strings attached. Just honest advice on how to improve your technology and reduce costs."
        ctaText="Get Started Now"
        ctaLink="/contact"
      />
    </div>
  );
};

export default FreeAssessment;
